import api from './api';

export interface ApiUser {
  id: number;
  email: string;
  password_hash?: string;
  phone?: string;
  phoneNumber?: string; // Alias pour la compatibilité
  // Rôles supportés par l'application
  role: 'admin' | 'super_admin' | 'agent' | 'technician' | 'customer' | 'manager';
  // Statuts supportés
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  first_name?: string;
  last_name?: string;
  firstName?: string; // Alias pour first_name
  lastName?: string;  // Alias pour last_name
  last_login?: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  profile_image?: string | null;
  specialty?: string; // Pour les techniciens (legacy)
  specialization?: string; // Pour les techniciens
  certification?: string; // Certification du technicien
  experience_years?: number; // Années d'expérience
  availability_status?: 'available' | 'busy' | 'offline' | string;
  preferences?: Record<string, any>;
  created_at: string;
  updated_at: string;
  createdAt?: string; // Alias pour created_at
  updatedAt?: string; // Alias pour updated_at
  deleted_at?: string | null;
  // Champs additionnels potentiellement renvoyés par certaines routes ou futures évolutions
  address?: string;
  city?: string;
  postal_code?: string; // Format API snake_case
  postalCode?: string;  // Alias camelCase
  country?: string;
  department?: string;
  position?: string;
  notes?: string;
}

export interface UserFilters {
  role?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: ApiUser[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class UsersService {
  // Récupérer la liste des utilisateurs
  async getUsers(filters: UserFilters = {}): Promise<UsersResponse> {
    try {
      console.log('Appel API GET /users (raw filters):', filters);
      const token = localStorage.getItem('token');
      console.log('Token utilisé pour la requête:', token ? 'Présent' : 'Manquant');

      // Ne pas envoyer les filtres vides (''), sinon le backend peut interpréter comme valeur exacte vide
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null && v !== '')
      );
      console.log('Filtres nettoyés envoyés à /users:', cleanedFilters);

      const response = await api.get<UsersResponse>('/users', {
        params: cleanedFilters,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Réponse API /users:', response);
      
      if (!response.data) {
        throw new Error('Aucune donnée reçue du serveur');
      }
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      // Retourner un objet vide avec la structure attendue en cas d'erreur
      return {
        success: false,
        data: {
          users: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        }
      };
    }
  }

  // Récupérer un utilisateur par son ID
  async getUserById(id: number): Promise<ApiUser> {
    const response = await api.get<{ success: boolean; data: ApiUser }>(`/users/${id}`);
    return response.data.data;
  }

  // Créer un nouvel utilisateur
  async createUser(userData: {
    email?: string;
    password: string;
    phone: string;
    role: 'admin' | 'super_admin' | 'agent' | 'technician' | 'customer' | 'manager';
    status?: 'active' | 'inactive' | 'pending' | 'suspended';
    first_name?: string;
    last_name?: string;
    send_welcome_email?: boolean;
    profile_image?: string;
  }): Promise<ApiUser> {
    const payload = {
      email: userData.email || undefined,
      password: userData.password,
      phone: userData.phone,
      role: userData.role, // Envoyer le rôle tel quel
      first_name: userData.first_name,
      last_name: userData.last_name,
      profile_image: userData.profile_image || undefined,
    };

    const response = await api.post<{ success: boolean; user?: ApiUser; data?: { user: ApiUser }; accessToken: string }>('/auth/register', payload);
    console.log('createUser response:', response.data);
    // L'API peut retourner user directement ou dans data.user
    const user = response.data.user || response.data.data?.user;
    if (!user) {
      throw new Error('User not found in response');
    }
    return user;
  }

  // Mettre à jour un utilisateur
  async updateUser(id: number, userData: {
    email?: string;
    password?: string;
    phone?: string | null;
    role?: 'admin' | 'super_admin' | 'agent' | 'technician' | 'customer' | 'manager';
    status?: 'active' | 'inactive' | 'pending' | 'suspended';
    first_name?: string;
    last_name?: string;
    email_verified?: boolean;
    phone_verified?: boolean;
    profile_image?: string | null;
    preferences?: Record<string, any>;
  }): Promise<ApiUser> {
    // Filtrer les champs non définis pour éviter d'envoyer des valeurs vides
    const payload = Object.entries(userData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    const response = await api.put<{ success: boolean; data: ApiUser }>(`/users/${id}`, payload);
    return response.data.data;
  }

  async deleteUser(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }

  // Mettre à jour le statut d'un utilisateur
  async updateUserStatus(id: number, data: { status: string }): Promise<{ success: boolean; data: ApiUser }> {
    const response = await api.patch(`/users/${id}/status`, data);
    return response.data;
  }
}

export const usersService = new UsersService();
